import { t, Trans } from "@lingui/macro";
import {
  UiBackdrop,
  UiButton,
  UiCircularProgress,
  UiDialogResponsive,
  UiPageFailure,
  UiTypography,
} from "@smp/ui";
import { REACT_APP__BASE_PATH } from "@smp/utils";
import clsx from "clsx";
import {
  ComponentProps,
  FC,
  lazy,
  Suspense,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";

import { PageNotFound } from "@/components/feedback/pages/page_not_found";
import { useDialog } from "@/hooks/use_dialog";
import { useEpOrganizationOrganizationList } from "@/hooks/use_ep_organization";
import { DialogNoCreationPermissions } from "@/screens/app/components/dialogs/dialog_no_creation_permissions";
import { config } from "@/screens/app/components/dialogs/dialog_pc/config";
import { useContextApp } from "@/screens/app/context/app";

import { ProviderDialogPc } from "./context";
import { IProviderDialogPcProps } from "./context/dialog_pc.types";
import classes from "./dialog_pc.module.css";
import { IDialogPcProps } from "./dialog_pc.types";

const DialogPcLazy = lazy(
  () => import(/* webpackChunkName: "dialog-pc" */ "./dialog_pc_lazy"),
);

export const DialogPc: FC<
  {
    isOpen: boolean;
    onClose: (redirect?: string) => void;
  } & Pick<
    IProviderDialogPcProps,
    | "postProjectId"
    | "groupId"
    | "pageConfigId"
    | "scheduleTime"
    | "mediaSet"
    | "initialText"
    | "storedStateKey"
    | "onCreatePostProject"
    | "isAdvisorSource"
    | "loaderText"
  >
> = ({
  isOpen,
  onClose,
  loaderText,
  onCreatePostProject,
  postProjectId,
  pageConfigId,
  groupId,
  scheduleTime,
  mediaSet,
  initialText,
  storedStateKey,
  isAdvisorSource,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const beforeOpenPathname = useRef(config.baseRoute);
  const [isDialogReady, setDialogReady] = useState(false);
  const [error, setError] = useState<
    | Parameters<ComponentProps<typeof ProviderDialogPc>["onError"]>[0]
    | undefined
  >();

  const {
    state: { organizationId },
  } = useContextApp();

  const { organizationList } = useEpOrganizationOrganizationList();

  const {
    close: closePcDialog,
    open: openPcDialog,
    isOpen: isPcDialogOpen,
  } = useDialog();

  const {
    close: closeNoPermissionsDialog,
    open: openNoPermissionsDialog,
    isOpen: isNoPermissionsDialogOpen,
  } = useDialog();

  const organization = useMemo(
    () =>
      organizationList &&
      organizationList.find(
        (organization) => organization.id === organizationId,
      ),
    [organizationId, organizationList],
  );

  const isReady = useMemo(
    () => isNoPermissionsDialogOpen || isDialogReady,
    [isNoPermissionsDialogOpen, isDialogReady],
  );

  const isReadonly = useMemo(
    () => organization?.role === "observer_v1",
    [organization?.role],
  );

  const handleOpen = useCallback(() => {
    if (!window.location.pathname.startsWith(config.dialogRoute)) {
      beforeOpenPathname.current = location.pathname;

      window.history.pushState(
        null,
        "",
        config.dialogRoute + (postProjectId ? `/${postProjectId}` : ""),
      );
    }

    if (postProjectId) {
      openPcDialog();
      return;
    }

    if (organization?.role === "observer_v1") {
      openNoPermissionsDialog();
    } else {
      openPcDialog();
    }
  }, [
    postProjectId,
    organization?.role,
    location.pathname,
    openPcDialog,
    openNoPermissionsDialog,
  ]);

  const handleClose = useCallback(() => {
    closePcDialog();
    closeNoPermissionsDialog();

    onClose();

    setDialogReady(false);
  }, [closePcDialog, closeNoPermissionsDialog, onClose]);

  const closeWithRedirect = useCallback(
    (path?: string) => {
      handleClose();

      if (path) {
        navigate(path);
      } else {
        window.history.replaceState(
          null,
          "",
          `${REACT_APP__BASE_PATH}/${beforeOpenPathname.current}`.replace(
            /\/\//g,
            "/",
          ),
        );
      }
    },
    [handleClose, navigate],
  );

  useLayoutEffect(() => {
    if (isOpen) {
      handleOpen();
    } else {
      setError(undefined);
    }
  }, [handleOpen, isOpen]);

  if (!isOpen) {
    return null;
  }

  if (error) {
    const isNotFound = error?.response.status === 404;

    return (
      <UiDialogResponsive
        open={isOpen}
        isButtonCloseHidden
        onClose={handleClose}
        classes={{
          paper: clsx(
            classes.errorDialog,
            !isNotFound && classes.errorDialog_other,
            isNotFound && classes.errorDialog_404,
          ),
        }}
      >
        {isNotFound ? (
          <>
            <PageNotFound />
            <UiButton
              variant="semifilled"
              className={classes.errorDialog__btn}
              onClick={handleClose}
            >
              <Trans>Close</Trans>
            </UiButton>
          </>
        ) : (
          <UiPageFailure
            errorMessage={
              error.response.errors?.at(0)?.message ||
              t`Something went wrong... Try your request again later`
            }
            title={<Trans>Error</Trans>}
            footer={
              <UiButton
                variant="semifilled"
                className={classes.errorDialog__btn}
                onClick={handleClose}
              >
                <Trans>Close</Trans>
              </UiButton>
            }
          />
        )}
      </UiDialogResponsive>
    );
  }

  return (
    <>
      {isPcDialogOpen && (
        <Suspense>
          <DialogPcLazy
            ComponentProps={{
              isOpen: true,
              onClose: closeWithRedirect,
            }}
            ProviderProps={{
              onInit: () => setDialogReady(true),
              onError: setError,
              onCreatePostProject,
              isReadonly,
              postProjectId,
              groupId,
              pageConfigId,
              scheduleTime,
              mediaSet,
              initialText,
              storedStateKey,
              isAdvisorSource,
            }}
          />
        </Suspense>
      )}

      <DialogNoCreationPermissions
        isOpen={isNoPermissionsDialogOpen}
        closeDialog={handleClose}
      />

      {!isReady &&
        createPortal(
          <UiBackdrop open={true} className={classes.backdrop}>
            <UiCircularProgress color={!!loaderText ? "inherit" : "primary"} />

            {loaderText && (
              <UiTypography className={classes.backdrop__text}>
                {loaderText}
              </UiTypography>
            )}
          </UiBackdrop>,
          document.body,
        )}
    </>
  );
};

export type { IDialogPcProps };
